import {call, put, takeEvery} from 'redux-saga/effects';
import {PayloadAction} from "@reduxjs/toolkit";
import {api} from "../../api/api";
import {cardsActions, MarkCardPayload, MarkSubcardPayload, UnmarkSubcardPayload} from "../cards.slice";
import {searchActions} from "../search/search.slice";
import {CardMarkDto, UnmarkResult} from "../../common";

/** Puts the card's current mark (null: nothing marked any more) into the card and search stores. */
function* applyMark(cardId: string, mark: CardMarkDto | null) {
    if (mark !== null) {
        yield put(cardsActions.setMark({cardId, mark}));
        yield put(searchActions.markItem({cardId, mark}));
    } else {
        yield put(cardsActions.setMark({cardId, mark: undefined}));
        yield put(searchActions.clearMarks([cardId]));
    }
}

function* markSaga(action: PayloadAction<MarkCardPayload>) {
    const {cardId, note} = action.payload;
    try {
        const mark: CardMarkDto = yield call(api.markCard, {cardId, note});
        yield call(applyMark, cardId, mark);
    } catch (error) {
        window.alert(error instanceof Error ? error.message : 'Failed to mark card');
    }
}

function* unmarkWholeCardSaga(action: PayloadAction<string>) {
    const cardId = action.payload;
    try {
        const {mark}: UnmarkResult = yield call(api.unmarkWholeCard, cardId);
        yield call(applyMark, cardId, mark);
    } catch (error) {
        window.alert(error instanceof Error ? error.message : 'Failed to remove the mark');
    }
}

function* markSubcardSaga(action: PayloadAction<MarkSubcardPayload>) {
    const {cardId} = action.payload;
    try {
        const mark: CardMarkDto = yield call(api.markSubcard, action.payload);
        yield call(applyMark, cardId, mark);
    } catch (error) {
        window.alert(error instanceof Error ? error.message : 'Failed to mark the subcard');
    }
}

function* unmarkSubcardSaga(action: PayloadAction<UnmarkSubcardPayload>) {
    const {cardId} = action.payload;
    try {
        const {mark}: UnmarkResult = yield call(api.unmarkSubcard, action.payload);
        yield call(applyMark, cardId, mark);
    } catch (error) {
        window.alert(error instanceof Error ? error.message : 'Failed to remove the mark');
    }
}

function* unmarkSaga(action: PayloadAction<string[]>) {
    const cardIds = action.payload;
    try {
        yield call(api.unmarkCards, cardIds);
        for (const cardId of cardIds) {
            yield put(cardsActions.setMark({cardId, mark: undefined}));
        }
        yield put(searchActions.clearMarks(cardIds));
    } catch (error) {
        window.alert(error instanceof Error ? error.message : 'Failed to remove the mark');
    }
}

export function* markCardSaga() {
    yield takeEvery(cardsActions.markCard.type, markSaga);
    yield takeEvery(cardsActions.unmarkWholeCard.type, unmarkWholeCardSaga);
    yield takeEvery(cardsActions.markSubcard.type, markSubcardSaga);
    yield takeEvery(cardsActions.unmarkSubcard.type, unmarkSubcardSaga);
    yield takeEvery(cardsActions.unmarkCards.type, unmarkSaga);
}
